import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  XMarkIcon, 
  BanknotesIcon, 
  CheckCircleIcon,
  ArrowRightIcon,
  ExclamationTriangleIcon,
  CurrencyRupeeIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline'

const BankIntegrationPopup = ({ isOpen, onClose, onSetupBank, restaurantName }) => {
  const [currentStep, setCurrentStep] = useState(0)

  const benefits = [
    {
      icon: CurrencyRupeeIcon,
      title: "Direct Settlements",
      description: "Get 96.5% of payments directly to your bank account",
      color: "text-green-600 bg-green-50"
    },
    {
      icon: ShieldCheckIcon,
      title: "Secure & Verified",
      description: "Bank-grade security with Cashfree integration",
      color: "text-blue-600 bg-blue-50"
    },
    {
      icon: BanknotesIcon,
      title: "T+1 Settlements",
      description: "Next day settlement at 11:00 AM automatically",
      color: "text-purple-600 bg-purple-50"
    }
  ]

  const steps = [
    {
      title: "Add Bank Account",
      description: "Verify your business bank account details with IFSC validation",
      status: "pending",
      icon: "🏦",
      details: "Real-time bank account verification (2-5 seconds)"
    },
    {
      title: "Submit KYC Documents", 
      description: "Provide PAN card and GST number for business verification",
      status: "pending",
      icon: "📋",
      details: "Required: Valid PAN (Format: ABCDE1234F) and GST number"
    },
    {
      title: "Cashfree Validation",
      description: "Automated validation of bank and KYC details",
      status: "pending",
      icon: "🔍",
      details: "Usually completes within 24-48 hours"
    },
    {
      title: "Start Receiving Payments",
      description: "96.5% direct settlements with T+1 processing",
      status: "pending",
      icon: "💰",
      details: "Next day settlements at 11:00 AM automatically"
    }
  ]

  if (!isOpen) return null

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-lg">
                  <BanknotesIcon className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Setup Split Payments</h2>
                  <p className="text-sm text-orange-100">Enable direct bank settlements for {restaurantName}</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <XMarkIcon className="h-5 w-5 text-white" />
              </button>
            </div>
          </div>

          <div className="p-6">
            {/* Warning Banner */}
            <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
              <div className="flex items-start gap-3">
                <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-amber-800 mb-1">Bank Integration Required</h3>
                  <p className="text-sm text-amber-700">
                    Your restaurant needs bank account integration to receive payments directly. 
                    Without this, you'll miss out on automatic settlements and split payment benefits.
                  </p>
                </div>
              </div>
            </div>

            {/* Benefits Grid */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Why Setup Split Payments?</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {benefits.map((benefit, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="p-4 bg-white border border-gray-200 rounded-xl hover:shadow-md transition-shadow"
                  >
                    <div className="p-2 rounded-lg bg-orange-50 text-orange-600">
                      <benefit.icon className="h-5 w-5" />
                    </div>
                    <h4 className="font-semibold text-gray-900 mb-1">{benefit.title}</h4>
                    <p className="text-sm text-gray-600">{benefit.description}</p>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Setup Steps */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Setup Process</h3>
              <div className="space-y-3">
                {steps.map((step, index) => (
                  <div key={index} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center text-sm font-semibold">
                        {index + 1}
                      </div>
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900">{step.title}</h4>
                      <p className="text-sm text-gray-600">{step.description}</p>
                    </div>
                    <CheckCircleIcon className="h-5 w-5 text-gray-300" />
                  </div>
                ))}
              </div>
            </div>

            {/* Revenue Split Visualization */}
            <div className="mb-6 p-4 bg-gradient-to-r from-orange-50 to-red-50 rounded-xl border border-orange-200">
              <h3 className="font-semibold text-gray-900 mb-3">Revenue Split Model</h3>
              <div className="grid grid-cols-3 gap-3">
                <div className="text-center p-3 bg-white rounded-lg border">
                  <p className="text-sm text-gray-600 mb-1">Customer Pays</p>
                  <p className="text-2xl font-bold text-gray-900">100%</p>
                  <p className="text-xs text-gray-500">Full amount</p>
                </div>
                <div className="text-center p-3 bg-white rounded-lg border border-orange-200">
                  <p className="text-sm text-orange-600 mb-1">You Receive</p>
                  <p className="text-2xl font-bold text-orange-900">96.5%</p>
                  <p className="text-xs text-orange-600">Direct to bank</p>
                </div>
                <div className="text-center p-3 bg-white rounded-lg border border-red-200">
                  <p className="text-sm text-red-600 mb-1">Platform Fee</p>
                  <p className="text-2xl font-bold text-red-900">3.5%</p>
                  <p className="text-xs text-red-600">Service charge</p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={onClose}
                className="flex-1 px-6 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl font-medium transition-colors"
              >
                Maybe Later
              </button>
              <button
                onClick={() => {
                  onSetupBank()
                  onClose()
                }}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-xl font-medium transition-all duration-200 flex items-center justify-center gap-2"
              >
                <span>Open Bank Management</span>
                <ArrowRightIcon className="h-4 w-4" />
              </button>
            </div>

            {/* Footer Note */}
            <div className="mt-4 p-3 bg-orange-50 rounded-lg">
              <p className="text-xs text-orange-700 text-center">
                🔒 Your bank details are secured with 256-bit encryption and processed by Cashfree Payments
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  )
}

export default BankIntegrationPopup
